import { useRef, useState } from 'react';
import { Form, Field, FormSpy } from 'react-final-form';
import { ParagraphMedium } from '../../../../../core/components';
import { NumberInputField } from '../../../../../core/components/form';
import { UniversalParameterProps } from '../../../models';
import type { ModalProps } from './patient-settings-modal';

type ContrastSectionProps = Pick<ModalProps, 'config' | 'onSubmit' | 'contrastParameters'>;

export const ContrastSection = ({ contrastParameters, onSubmit }: ContrastSectionProps): JSX.Element => {
  const [isActive, setIsActive] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const patientWeight = 80;

  const initial: UniversalParameterProps = {};
  contrastParameters.forEach((p) => {
    initial[p.name] = p.selectedValue;
  });

  const possibleValues = [
    { id: 1, label: '1', value: '1' },
    { id: 2, label: '0.5', value: '0.5' },
  ];

  return (
    <div className="my-4">
      <div className="col-10"></div>
      {
        <div className="settings-modal__form-wrapper">
          <ParagraphMedium className="m-0">{`Select which contrast settings you wish to use`}</ParagraphMedium>
          <Form
            onSubmit={onSubmit}
            initialValues={initial}
            mutators={{
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              setFormValue: ([field, value]: any, state, { changeValue }) => {
                changeValue(state, field, () => value);
              },
            }}
            render={({ handleSubmit }) => (
              <form onSubmit={handleSubmit} onChange={handleSubmit} className="d-flex justify-content-start my-2">
                <FormSpy
                  subscription={{ values: true, active: true }}
                  onChange={(state) => {
                    if (!!state.active !== isActive) {
                      // blur
                      handleSubmit();
                      setIsActive(!isActive);
                    }
                  }}
                />
                <div className="py-0 mt-4 mb-3 flex-fill settings-modal-form" ref={contentRef}>
                  <div className="row">
                    <div className="col-4">
                      <div className="form-group">
                        <div className="row">
                          <div className="col">
                            <label htmlFor="contrast_agent_concentration">Contrast agent concentration [mmol/ml]</label>
                          </div>
                        </div>
                        <div className="form-label-group">
                          <Field
                            component="select"
                            label="Contrast agent concentration [mmol/ml]"
                            name="contrast_concentration"
                            className="form-control"
                            defaultValue={contrastParameters[0]?.selectedValue}
                          >
                            {possibleValues.map((option) => {
                              return (
                                <option key={option.id} value={option.value}>
                                  {option.label}
                                </option>
                              );
                            })}
                          </Field>
                        </div>
                      </div>
                    </div>
                    <div className="col-4">
                      <NumberInputField
                        type="number"
                        selValue={parseInt(contrastParameters[1]?.selectedValue, 10)}
                        label="Contrast agent volume [ml]"
                        name="contrast_volume"
                        stepValue={1}
                        min={1}
                        max={30}
                        showTooltip
                      />
                    </div>
                    <div className="col-4">
                      <NumberInputField
                        type="number"
                        selValue={parseInt(contrastParameters[2]?.selectedValue, 10)}
                        label="Time after contrast [min]"
                        name="time_after_contrast"
                        stepValue={1}
                        min={1}
                        max={20}
                        showTooltip
                      />
                    </div>
                  </div>
                  <div className="row my-2">
                    <div className="col-6">
                      <label htmlFor="patient_weight">Patient Weight</label>
                      <p className="settings-text-label">{patientWeight} kg</p>
                    </div>
                    <div className="col-6">
                      {
                        <>
                          <label htmlFor="calculated_dose">Calculated Dose</label>
                          {contrastParameters[0]?.selectedValue && parseFloat(contrastParameters[1]?.selectedValue) && (
                            <p className="settings-text-label">
                              {(parseFloat(contrastParameters[0]?.selectedValue) *
                                parseFloat(contrastParameters[1]?.selectedValue)) /
                                patientWeight}{' '}
                              {` mmol/kg`}
                            </p>
                          )}
                        </>
                      }
                    </div>
                  </div>
                </div>
              </form>
            )}
          />
        </div>
      }
    </div>
  );
};
